import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMobile } from '../../context/MobileContext';
import { getDeletionRequests, approveDeletion, denyDeletion, getConversations, addCaseNote, createCommunication, getCases, getCustomLists } from '../../utils/api';
import { Bell, Trash2, X, AlertTriangle, Menu, Search, User, Briefcase, Sun, Moon, MessageCircle, FileText, Phone } from 'lucide-react';
import toast from 'react-hot-toast';

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Global Search Component ───────────────────────────────────────────────────
function GlobalSearch() {
  const navigate  = useNavigate();
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    try {
      const BASE  = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('zeus_token') || '';
      const res   = await fetch(`${BASE}/search?q=${encodeURIComponent(q)}&_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) { setResults({ cases: [], providers: [] }); setLoading(false); return; }
      const data = await res.json();
      console.log('[ZeusSearch] cases:', data.cases?.length, 'providers:', data.providers?.length, 'debug:', JSON.stringify(data._debug));
      setResults(data);
    } catch (err) {
      console.error('[ZeusSearch] error:', err);
      setResults({ cases: [], providers: [] });
    } finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    clearTimeout(timerRef.current);
    if (!val.trim()) { setResults(null); setLoading(false); return; }
    setLoading(true); // show spinner immediately
    timerRef.current = setTimeout(() => doSearch(val.trim()), 150);
  };

  const goToCase = (c) => {
    setOpen(false); setQuery(''); setResults(null);
    navigate(`/cases/${c.id}`);
  };

  const goToProvider = (p) => {
    setOpen(false); setQuery(''); setResults(null);
    // NetworkPage has no /network/:id route — pass provider via location state
    // so NetworkPage can auto-open the edit drawer for that provider
    navigate('/network', { state: { openProviderId: p.id } });
  };

  const hasResults = results && (results.cases?.length > 0 || results.providers?.length > 0);
  const noResults  = results && !hasResults && !loading;

  return (
    <div ref={wrapRef} className="header-search-wrap">
      <div className={`header-search-input${open ? ' focused' : ''}`}>
        <Search size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
        <input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={() => { setOpen(true); if (query.length >= 2) doSearch(query); }}
          placeholder="Search cases, providers, DOI, DOB…"
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none',
            color: 'var(--text-primary)', fontSize: '0.82rem', fontFamily: 'inherit',
          }}
        />
        {query ? (
          <button onClick={() => { setQuery(''); setResults(null); inputRef.current?.focus(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}>
            <X size={12} />
          </button>
        ) : (
          <span style={{
            fontSize: '0.6rem', color: '#9ca3af', padding: '1px 4px',
            background: '#f3f4f6', borderRadius: 3, whiteSpace: 'nowrap', flexShrink: 0,
            border: '1px solid #e5e7eb',
          }}>⌘K</span>
        )}
      </div>

      {open && query.length >= 2 && (
        <div style={{
          position: 'absolute', zIndex: 99999,
          top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
          width: 400, maxHeight: 500, overflowY: 'auto',
          background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
          borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}>
          {/* Loading */}
          {loading && (
            <div style={{ padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted)', fontSize: '0.84rem' }}>
              <div className="spinner" style={{ width: 15, height: 15, borderWidth: 2 }} />
              Searching…
            </div>
          )}

          {/* No results */}
          {noResults && (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <Search size={28} style={{ color: 'var(--text-muted)', opacity: 0.3, marginBottom: 10 }} />
              <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>No results</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Try name, case #, claim #, DOI, or DOB (MM/DD/YYYY)</div>
            </div>
          )}

          {/* Results */}
          {hasResults && !loading && (
            <>
              {results.cases.length > 0 && (
                <div>
                  <div style={{ padding: '10px 14px 4px', fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--zeus-gold)' }}>
                    Cases ({results.cases.length})
                  </div>
                  {results.cases.map(c => (
                    <div key={c.id} onClick={() => goToCase(c)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: 'rgba(212,168,83,0.12)', border: '1px solid rgba(212,168,83,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Briefcase size={15} style={{ color: 'var(--zeus-gold)' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.client_name || 'Unnamed'}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                          {c.case_number && <span>{c.case_number}</span>}
                          {c.date_of_incident && <span>DOI: {formatDate(c.date_of_incident)}</span>}
                          {c.date_of_birth && <span>DOB: {formatDate(c.date_of_birth)}</span>}
                          {c.claim_number && <span>Claim: {c.claim_number}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', flexShrink: 0, textTransform: 'capitalize' }}>
                        {c.status || 'intake'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {results.providers.length > 0 && (
                <div style={{ borderTop: results.cases.length > 0 ? '1px solid var(--zeus-border)' : 'none' }}>
                  <div style={{ padding: '10px 14px 4px', fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--zeus-gold)' }}>
                    Providers ({results.providers.length})
                  </div>
                  {results.providers.map(p => (
                    <div key={p.id} onClick={() => goToProvider(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <User size={15} style={{ color: '#0d9488' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.name}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', gap: 8, marginTop: 2 }}>
                          {p.provider_type && <span>{p.provider_type}</span>}
                          {p.specialty && <span>· {p.specialty}</span>}
                          {p.city && <span>· {p.city}{p.state ? `, ${p.state}` : ''}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ padding: '8px 14px', borderTop: '1px solid var(--zeus-border)', display: 'flex', gap: 14, fontSize: '0.67rem', color: 'var(--text-muted)' }}>
                <span>Click to open</span>
                <span>Esc to close</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Case Search Helper (shared by QuickNote & QuickComm) ────────────────────
function useCaseSearch() {
  var cases = useState([]);
  var caseList = cases[0]; var setCaseList = cases[1];
  var sq = useState('');
  var searchQ = sq[0]; var setSearchQ = sq[1];
  var sel = useState(null);
  var selected = sel[0]; var setSelected = sel[1];
  var dd = useState(false);
  var dropOpen = dd[0]; var setDropOpen = dd[1];
  var ref = useRef(null);
  var timer = useRef(null);

  function doSearch(q) {
    var params = { per_page: 25 };
    if (q && q.trim()) params.search = q.trim();
    getCases(params)
      .then(function(res) {
        var list = [];
        if (res && Array.isArray(res.cases)) list = res.cases;
        else if (Array.isArray(res)) list = res;
        setCaseList(list);
      })
      .catch(function() { setCaseList([]); });
  }

  // Load initial cases on first open
  function loadCases() { doSearch(''); }

  // Debounced search when typing
  function onSearchChange(val) {
    setSearchQ(val);
    setDropOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(function() { doSearch(val); }, 250);
  }

  var filtered = caseList;

  function selectCase(c) {
    setSelected(c);
    setSearchQ(c.case_number + ' - ' + c.client_name);
    setDropOpen(false);
  }

  function clear() { setSelected(null); setSearchQ(''); doSearch(''); }

  useEffect(function() {
    if (!dropOpen) return;
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setDropOpen(false); }
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [dropOpen]);

  return { caseList: caseList, searchQ: searchQ, setSearchQ: onSearchChange, selected: selected, setSelected: setSelected, dropOpen: dropOpen, setDropOpen: setDropOpen, ref: ref, loadCases: loadCases, filtered: filtered, selectCase: selectCase, clear: clear };
}

// ── Quick Note ──────────────────────────────────────────────────────────────
function QuickNote() {
  var ov = useState(false);
  var open = ov[0]; var setOpen = ov[1];
  var nt = useState('');
  var noteContent = nt[0]; var setNoteContent = nt[1];
  var tp = useState('General');
  var noteType = tp[0]; var setNoteType = tp[1];
  var nto = useState(['General','Attorney','Medical','Follow-up','Internal']);
  var noteTypeOptions = nto[0]; var setNoteTypeOptions = nto[1];
  var sv = useState(false);
  var saving = sv[0]; var setSaving = sv[1];
  var panelRef = useRef(null);
  var cs = useCaseSearch();
  var typesLoaded = useRef(false);

  useEffect(function() {
    if (!open) return;
    function handler(e) { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [open]);

  function handleOpen() {
    setOpen(!open);
    cs.loadCases();
    cs.clear();
    setNoteContent('');
    setNoteType('General');
    // Load note types from custom lists
    if (!typesLoaded.current) {
      getCustomLists().then(function(data) {
        if (data && data.note_type_options && data.note_type_options.length) {
          setNoteTypeOptions(data.note_type_options);
        }
        typesLoaded.current = true;
      }).catch(function() {});
    }
  }

  function handleSave() {
    if (!cs.selected) { toast.error('Select a case first'); return; }
    if (!noteContent.trim()) { toast.error('Enter a note'); return; }
    setSaving(true);
    addCaseNote(cs.selected.id || cs.selected._id, noteContent.trim(), noteType, '')
      .then(function() {
        toast.success('Note added to ' + cs.selected.case_number);
        setOpen(false);
      })
      .catch(function(e) { toast.error((e && e.message) || 'Failed to save note'); })
      .finally(function() { setSaving(false); });
  }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button onClick={handleOpen} className="notif-btn" title="Quick Note">
        <FileText size={16} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 340, zIndex: 99999,
          background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
          borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.5)', padding: '16px',
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={15} style={{ color: 'var(--zeus-gold)' }} /> Quick Note
          </div>

          {/* Case search */}
          <div ref={cs.ref} style={{ position: 'relative', marginBottom: 10 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Case</label>
            {cs.selected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 8, background: 'var(--zeus-gold-dim)', border: '1px solid rgba(212,168,83,0.2)' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--zeus-gold)' }}>{cs.selected.case_number}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', flex: 1 }}>{cs.selected.client_name}</span>
                <button onClick={cs.clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}><X size={13} /></button>
              </div>
            ) : (
              <input className="form-input" placeholder="Search case # or name..." value={cs.searchQ}
                onChange={function(e) { cs.setSearchQ(e.target.value); cs.setDropOpen(true); }}
                onFocus={function() { cs.setDropOpen(true); }}
                style={{ fontSize: '0.82rem', padding: '7px 10px' }}
              />
            )}
            {cs.dropOpen && !cs.selected && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                {cs.filtered.length === 0 ? (
                  <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.78rem' }}>No cases found</div>
                ) : cs.filtered.map(function(c) {
                  return (
                    <div key={c.id || c._id} onClick={function() { cs.selectCase(c); }}
                      style={{ padding: '7px 12px', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                      onMouseOver={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseOut={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--zeus-gold)' }}>{c.case_number}</span>
                      <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{c.client_name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Note type */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Type</label>
            <select className="form-select" value={noteType} onChange={function(e) { setNoteType(e.target.value); }} style={{ fontSize: '0.82rem', padding: '6px 10px' }}>
              {noteTypeOptions.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
            </select>
          </div>

          {/* Note content */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Note</label>
            <textarea className="form-input" rows={4} value={noteContent} onChange={function(e) { setNoteContent(e.target.value); }}
              placeholder="Type your note..." style={{ fontSize: '0.82rem', padding: '8px 10px', resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={function() { setOpen(false); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Note'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Quick Communication ─────────────────────────────────────────────────────
function QuickComm() {
  var ov = useState(false);
  var open = ov[0]; var setOpen = ov[1];
  var sv = useState(false);
  var saving = sv[0]; var setSaving = sv[1];
  var panelRef = useRef(null);
  var cs = useCaseSearch();

  var COMM_TYPES = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'letter', label: 'Letter/Mail' },
    { value: 'fax', label: 'Fax' },
    { value: 'text', label: 'Text Message' },
    { value: 'in_person', label: 'In Person' },
    { value: 'other', label: 'Other' },
  ];

  var initForm = { direction: 'outbound', comm_type: 'phone', contact_name: '', subject: '', summary: '', duration_minutes: '', comm_date: '' };
  var fm = useState(Object.assign({}, initForm));
  var form = fm[0]; var setForm = fm[1];

  useEffect(function() {
    if (!open) return;
    function handler(e) { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [open]);

  function handleOpen() {
    setOpen(!open);
    cs.loadCases();
    cs.clear();
    setForm(Object.assign({}, initForm, { comm_date: new Date().toISOString().slice(0, 16) }));
  }

  function updateField(field, value) {
    setForm(function(prev) { var next = Object.assign({}, prev); next[field] = value; return next; });
  }

  function handleSave() {
    if (!cs.selected) { toast.error('Select a case first'); return; }
    if (!form.comm_type) { toast.error('Select communication type'); return; }
    setSaving(true);
    createCommunication(Object.assign({}, form, { case_id: cs.selected.id || cs.selected._id }))
      .then(function() {
        toast.success('Communication logged on ' + cs.selected.case_number);
        setOpen(false);
      })
      .catch(function(e) { toast.error((e && e.message) || 'Failed to save'); })
      .finally(function() { setSaving(false); });
  }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button onClick={handleOpen} className="notif-btn" title="Quick Communication">
        <Phone size={16} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 360, zIndex: 99999,
          background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
          borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.5)', padding: '16px',
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Phone size={15} style={{ color: 'var(--zeus-gold)' }} /> Quick Communication
          </div>

          {/* Case search */}
          <div ref={cs.ref} style={{ position: 'relative', marginBottom: 10 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Case</label>
            {cs.selected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 8, background: 'var(--zeus-gold-dim)', border: '1px solid rgba(212,168,83,0.2)' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--zeus-gold)' }}>{cs.selected.case_number}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', flex: 1 }}>{cs.selected.client_name}</span>
                <button onClick={cs.clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}><X size={13} /></button>
              </div>
            ) : (
              <input className="form-input" placeholder="Search case # or name..." value={cs.searchQ}
                onChange={function(e) { cs.setSearchQ(e.target.value); cs.setDropOpen(true); }}
                onFocus={function() { cs.setDropOpen(true); }}
                style={{ fontSize: '0.82rem', padding: '7px 10px' }}
              />
            )}
            {cs.dropOpen && !cs.selected && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                {cs.filtered.length === 0 ? (
                  <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.78rem' }}>No cases found</div>
                ) : cs.filtered.map(function(c) {
                  return (
                    <div key={c.id || c._id} onClick={function() { cs.selectCase(c); }}
                      style={{ padding: '7px 12px', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                      onMouseOver={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseOut={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--zeus-gold)' }}>{c.case_number}</span>
                      <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{c.client_name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Direction + Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Direction</label>
              <select className="form-select" value={form.direction} onChange={function(e) { updateField('direction', e.target.value); }} style={{ fontSize: '0.82rem', padding: '6px 10px' }}>
                <option value="outbound">Outbound</option>
                <option value="inbound">Inbound</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Type</label>
              <select className="form-select" value={form.comm_type} onChange={function(e) { updateField('comm_type', e.target.value); }} style={{ fontSize: '0.82rem', padding: '6px 10px' }}>
                {COMM_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}
              </select>
            </div>
          </div>

          {/* Contact + Duration */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Contact</label>
              <input className="form-input" value={form.contact_name} onChange={function(e) { updateField('contact_name', e.target.value); }} placeholder="Name..." style={{ fontSize: '0.82rem', padding: '6px 10px' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Duration</label>
              <input className="form-input" type="number" value={form.duration_minutes} onChange={function(e) { updateField('duration_minutes', e.target.value); }} placeholder="min" style={{ fontSize: '0.82rem', padding: '6px 10px' }} />
            </div>
          </div>

          {/* Subject */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Subject</label>
            <input className="form-input" value={form.subject} onChange={function(e) { updateField('subject', e.target.value); }} placeholder="Brief subject..." style={{ fontSize: '0.82rem', padding: '6px 10px' }} />
          </div>

          {/* Summary */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Summary</label>
            <textarea className="form-input" rows={3} value={form.summary} onChange={function(e) { updateField('summary', e.target.value); }}
              placeholder="What was discussed..." style={{ fontSize: '0.82rem', padding: '8px 10px', resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={function() { setOpen(false); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Log Communication'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
export default function Header({ title, children }) {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { toggleSidebar } = useMobile();
  const isAdmin = ['super_admin', 'admin'].includes(user?.access_level || 'standard');

  // Theme toggle
  const [theme, setTheme] = useState(() => localStorage.getItem('zeus-theme') || 'light');
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('zeus-theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications,     setNotifications]     = useState([]);
  const [loadingNotifs,     setLoadingNotifs]     = useState(false);
  const [processingId,      setProcessingId]      = useState(null);
  const [unreadMsgCount,    setUnreadMsgCount]    = useState(0);
  const dropdownRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    if (!isAdmin) { setNotifications([]); return; }
    setLoadingNotifs(true);
    try {
      const reqs  = await getDeletionRequests();
      const items = (Array.isArray(reqs) ? reqs : []).map(r => ({
        id: r._id, type: 'deletion_request',
        title:       `Delete Case ${r.case_number || r.case_ref?.case_number || ''}`,
        subtitle:    r.client_name || r.case_ref?.client_name || 'Unnamed',
        reason:      r.reason || '',
        requestedBy: r.requested_by
          ? `${r.requested_by.first_name || ''} ${r.requested_by.last_name || ''}`.trim() || r.requested_by.email
          : 'Unknown',
        role: r.requested_by?.role || '',
        time: r.createdAt,
      }));
      setNotifications(items);
    } catch { setNotifications([]); }
    finally { setLoadingNotifs(false); }
  }, [isAdmin]);

  // Poll unread messages
  useEffect(() => {
    const fetchUnread = () => {
      getConversations().then(data => {
        const list = Array.isArray(data) ? data : [];
        setUnreadMsgCount(list.filter(c => c.has_unread).length);
      }).catch(() => {});
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);
  useEffect(() => {
    if (!isAdmin) return;
    const i = setInterval(loadNotifications, 60000);
    return () => clearInterval(i);
  }, [isAdmin, loadNotifications]);
  useEffect(() => {
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowNotifications(false); };
    if (showNotifications) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showNotifications]);

  const handleApprove = async (id) => {
    setProcessingId(id);
    try { await approveDeletion(id); toast.success('Case permanently deleted'); await loadNotifications(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
    finally { setProcessingId(null); }
  };
  const handleDeny = async (id) => {
    setProcessingId(id);
    try { await denyDeletion(id); toast.success('Deletion request denied'); await loadNotifications(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
    finally { setProcessingId(null); }
  };

  const count = notifications.length + unreadMsgCount;
  const [shaking, setShaking] = useState(false);
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (count > prevCountRef.current && prevCountRef.current >= 0) {
      setShaking(true);
      const t = setTimeout(() => setShaking(false), 3000);
      prevCountRef.current = count;
      return () => clearTimeout(t);
    }
    prevCountRef.current = count;
  }, [count]);
  const formatTime = (t) => {
    if (!t) return '';
    const diff = Math.floor((Date.now() - new Date(t)) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="app-header" style={{ position: 'relative' }}>
      <div className="app-header-left">
        <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Open menu">
          <Menu size={20} />
        </button>
        <h1 className="app-header-title">{title}</h1>
      </div>

      {/* Global Search — absolutely centered */}
      <GlobalSearch />

      <div className="app-header-right">
        {children}

        {/* ── Quick Note ── */}
        <QuickNote />

        {/* ── Quick Communication ── */}
        <QuickComm />

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="notif-btn"
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        {/* Notification Bell */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button onClick={() => setShowNotifications(!showNotifications)}
            className={`notif-btn${shaking ? ' bell-shake' : ''}`}
            title="Notifications"
          >
            <Bell size={19} />
            {count > 0 && <span className="notif-badge">{count > 99 ? '99+' : count}</span>}
          </button>

          {showNotifications && (
            <div className="notif-dropdown">
              <div className="notif-dropdown-header">
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Notifications</span>
                {count > 0 && <span style={{ fontSize: '0.72rem', padding: '2px 8px', background: 'var(--status-red)', color: '#fff', borderRadius: '10px', fontWeight: 600 }}>{count} pending</span>}
              </div>
              {loadingNotifs && !notifications.length && !unreadMsgCount ? (
                <div style={{ padding: '24px', textAlign: 'center' }}><div className="spinner" /></div>
              ) : count === 0 ? (
                <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Bell size={24} style={{ marginBottom: '8px', opacity: 0.4 }} />
                  <p style={{ fontSize: '0.85rem', margin: 0 }}>No new notifications</p>
                </div>
              ) : (
                <div>
                  {/* Unread Messages */}
                  {unreadMsgCount > 0 && (
                    <div
                      className="notif-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setShowNotifications(false);
                        const w = 820, h = 700;
                        const left = window.screenX + window.outerWidth - w - 40;
                        const top = window.screenY + 60;
                        window.open('/messenger', 'zeuscms-messenger', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`);
                      }}
                    >
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <MessageCircle size={14} style={{ color: '#3b82f6' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                            {unreadMsgCount} unread message{unreadMsgCount !== 1 ? 's' : ''}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Click to open Messenger</div>
                        </div>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}>{unreadMsgCount}</span>
                      </div>
                    </div>
                  )}
                  {/* Deletion Requests */}
                  {notifications.map(n => (
                    <div key={n.id} className="notif-item">
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                          <AlertTriangle size={14} style={{ color: 'var(--status-red)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{n.title}</span>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>{formatTime(n.time)}</span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 2 }}>{n.subtitle}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>by {n.requestedBy}{n.role ? ` (${n.role})` : ''}</div>
                          {n.reason && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 4, padding: '4px 8px', background: 'rgba(0,0,0,0.15)', borderRadius: 'var(--radius-sm)', fontStyle: 'italic' }}>"{n.reason}"</div>}
                          {isAdmin && n.type === 'deletion_request' && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                              <button onClick={() => handleDeny(n.id)} disabled={processingId === n.id} style={{ background: 'transparent', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', borderRadius: 'var(--radius-sm)', padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <X size={12} /> Deny
                              </button>
                              <button onClick={() => handleApprove(n.id)} disabled={processingId === n.id} style={{ background: 'var(--status-red)', border: 'none', color: '#fff', borderRadius: 'var(--radius-sm)', padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Trash2 size={12} /> {processingId === n.id ? '...' : 'Approve'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isAdmin && count > 0 && (
                <div style={{ padding: '10px 14px', borderTop: '1px solid var(--zeus-border)', textAlign: 'center' }}>
                  <button onClick={() => { setShowNotifications(false); navigate('/settings'); }} style={{ background: 'none', border: 'none', color: 'var(--zeus-gold)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500 }}>
                    View all in Settings
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
